import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../contexts/AuthContext';

const PAYMENT_STATE = {
  1: { label: 'Ожидание', bg: '#fef9c3', color: '#854d0e', dot: '#f59e0b' },
  2: { label: 'Готов',    bg: '#dcfce7', color: '#166534', dot: '#22c55e' },
  3: { label: 'Ошибка',   bg: '#fee2e2', color: '#991b1b', dot: '#ef4444' },
};

function PaymentStateBadge({ state }) {
  const cfg = PAYMENT_STATE[Number(state)] || { label: String(state ?? '—'), bg: '#f3f4f6', color: '#374151', dot: '#9ca3af' };
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        px: 1.25,
        py: 0.35,
        borderRadius: '20px',
        bgcolor: cfg.bg,
        color: cfg.color,
        fontSize: '0.75rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </Box>
  );
}

function formatPaymentAmount(payment) {
  const amount = Number(payment?.amount || 0);
  if (String(payment?.source || '').toLowerCase() === 'terbium') {
    const unit = amount > 100 ? 'MB' : 'GB';
    return `${amount.toLocaleString()} ${unit}`;
  }
  return (amount / 100).toLocaleString();
}

export default function TopUpPage() {
  const { user } = useAuth();
  const [providers, setProviders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [selectedProvider, setSelectedProvider] = React.useState(null);

  const [account, setAccount] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [email, setEmail] = React.useState('orders@terbium.pro');
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');
  const [submitSuccess, setSubmitSuccess] = React.useState('');
  const [createdInfo, setCreatedInfo] = React.useState(null);

  const [paymentsLoading, setPaymentsLoading] = React.useState(false);
  const [paymentsError, setPaymentsError] = React.useState('');
  const [payments, setPayments] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(0);
  const [balance, setBalance] = React.useState(null);
  const [reloadingBalance, setReloadingBalance] = React.useState(false);
  const [searchIdInput, setSearchIdInput] = React.useState('');
  const [isSearchMode, setIsSearchMode] = React.useState(false);
  const [selectedGb, setSelectedGb] = React.useState(false);
  const [gbPackageType, setGbPackageType] = React.useState('1500 MB');
  const [checkingIds, setCheckingIds] = React.useState([]);

  const gbImage = 'https://bahbit.org/img/providers/0Hwn4Bz5dswKYJY2NBu1T4iAqPNfM61oR.png';

  React.useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError('');
    fetch('/api/bahbit/providers', { method: 'GET' })
      .then(r => {
        if (!r.ok) throw new Error('Ошибка загрузки провайдеров');
        return r.json();
      })
      .then(data => {
        const list = data?.data?.providers || [];
        if (isMounted) { setProviders(Array.isArray(list) ? list : []); setLoading(false); }
      })
      .catch(e => {
        if (isMounted) { setError(e.message || 'Не удалось загрузить провайдеров'); setLoading(false); }
      });
    return () => { isMounted = false; };
  }, []);

  React.useEffect(() => {
    let active = true;
    fetch('/api/bahbit/local/balance')
      .then(r => r.json().catch(() => ({})))
      .then(data => {
        if (!active) return;
        const val = data?.data?.state?.terminal_balance;
        if (typeof val === 'number') setBalance(val);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const handleRefreshBalance = () => {
    setReloadingBalance(true);
    fetch('/api/bahbit/state', { method: 'POST' })
      .then(r => r.json().catch(() => ({})))
      .then(data => {
        const val = data?.data?.state?.terminal_balance;
        if (typeof val === 'number') setBalance(val);
      })
      .catch(() => {})
      .finally(() => setReloadingBalance(false));
  };

  const loadPayments = React.useCallback((p) => {
    let active = true;
    setPaymentsLoading(true);
    setPaymentsError('');
    fetch(`/api/payments?page=${p}`)
      .then(r => { if (!r.ok) throw new Error('Ошибка загрузки платежей'); return r.json(); })
      .then(data => {
        if (!active) return;
        const list = data?.data?.payments || [];
        const pagination = data?.data?.pagination || {};
        setPayments(Array.isArray(list) ? list : []);
        setTotalPages(Number(pagination.total_pages || 0));
        setPaymentsLoading(false);
      })
      .catch(e => {
        if (!active) return;
        setPaymentsError(e.message || 'Не удалось загрузить платежи');
        setPaymentsLoading(false);
      });
    return () => { active = false; };
  }, []);

  React.useEffect(() => {
    if (isSearchMode) return;
    const cleanup = loadPayments(page);
    return () => { if (typeof cleanup === 'function') cleanup(); };
  }, [page, loadPayments, isSearchMode]);

  React.useEffect(() => {
    const raw = (searchIdInput || '').trim();
    if (!raw) {
      if (isSearchMode) { setIsSearchMode(false); loadPayments(page); }
      return;
    }
    setIsSearchMode(true);
    setPaymentsLoading(true);
    setPaymentsError('');
    const handler = setTimeout(() => {
      const csv = raw.replace(/[^0-9,]+/g, ',').replace(/,+/g, ',').replace(/^,|,$/g, '');
      if (!csv) { setPayments([]); setTotalPages(0); setPaymentsLoading(false); return; }
      fetch(`/api/bahbit/payment/search?q=${encodeURIComponent(csv)}&limit=50`)
        .then(r => { if (!r.ok) throw new Error('Ошибка поиска платежа'); return r.json(); })
        .then(data => {
          const list = data?.data?.payments || [];
          const pagination = data?.data?.pagination || {};
          setPayments(Array.isArray(list) ? list : []);
          setTotalPages(Number(pagination.total_pages || 1));
          setPaymentsLoading(false);
        })
        .catch(e => {
          setPaymentsError(e.message || 'Не удалось найти платеж');
          setPayments([]); setTotalPages(0); setPaymentsLoading(false);
        });
    }, 800);
    return () => clearTimeout(handler);
  }, [searchIdInput, loadPayments, page, isSearchMode]);

  const digitsOnly = (value) => value.replace(/\D+/g, '');

  const providerRegex = React.useMemo(() => {
    if (!selectedProvider?.format?.regex) return null;
    try { return new RegExp('^' + selectedProvider.format.regex + '$'); } catch (_) { return null; }
  }, [selectedProvider]);

  const cleanedPlaceholder = React.useMemo(() => {
    if (!selectedProvider?.format) return '';
    const raw = String(selectedProvider.format.placeholder || '');
    const prefix = String(selectedProvider.format.prefix || '');
    let withoutPrefix = raw;
    if (prefix) withoutPrefix = withoutPrefix.replaceAll(prefix, '');
    return withoutPrefix.replace(/[^0-9xX]+/g, ' ').replace(/\s+/g, ' ').trim();
  }, [selectedProvider]);

  const isAccountValidProvider = React.useMemo(() => {
    if (!selectedProvider) return false;
    const val = digitsOnly(account);
    if (!val) return false;
    if (providerRegex) return providerRegex.test(val);
    return true;
  }, [account, providerRegex, selectedProvider]);

  const isAccountValidGb = React.useMemo(() => digitsOnly(account).length === 8, [account]);
  const isAccountValid = selectedGb ? isAccountValidGb : isAccountValidProvider;

  const minAmountMinor = selectedProvider?.min ?? 1;
  const maxAmountMinor = selectedProvider?.max ?? 99999999;
  const minAmount = minAmountMinor / 100;
  const maxAmount = maxAmountMinor / 100;
  const numAmount = Number(amount);
  const isAmountValid = Number.isInteger(numAmount) && numAmount >= minAmount && numAmount <= maxAmount;

  const handleSelect = (p) => { setSelectedProvider(p); setAccount(''); setAmount(''); setSubmitError(''); setSubmitSuccess(''); setCreatedInfo(null); };

  const handleSelectGb = () => {
    setSelectedGb(true); setSelectedProvider(null); setAccount('');
    setEmail('orders@terbium.pro'); setGbPackageType('1500 MB');
    setSubmitError(''); setSubmitSuccess(''); setCreatedInfo(null);
  };

  const handleBack = () => { setSelectedProvider(null); setSelectedGb(false); setCreatedInfo(null); };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedProvider || !isAccountValid || !isAmountValid) return;
    setSubmitError(''); setSubmitSuccess(''); setSubmitting(true);
    const payload = { amount: parseInt(amount || '0', 10) * 100, user_id: user?.id, provider_id: selectedProvider.id, account: digitsOnly(account), email };
    fetch('/api/bahbit/payment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(user?.api_token ? { Authorization: `Bearer ${user.api_token}` } : {}) },
      body: JSON.stringify(payload)
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok || data?.success === false) throw new Error(data?.error || 'Не удалось создать платеж');
        setSubmitSuccess('Платеж создан');
        setCreatedInfo({ provider_id: selectedProvider.id, provider_label: selectedProvider.label, account: digitsOnly(account), amount_units: numAmount, email });
        const placeholder = { local_id: '-', account: digitsOnly(account), amount: parseInt(amount || '0', 10) * 100, state: 1, error_message: '', provider_id: selectedProvider.id };
        setPayments(prev => [placeholder, ...prev]);
      })
      .catch((err) => setSubmitError(err.message || 'Ошибка создания платежа'))
      .finally(() => setSubmitting(false));
  };

  const handleSubmitGb = (e) => {
    e.preventDefault();
    if (!selectedGb || !account) return;
    setSubmitError(''); setSubmitSuccess(''); setSubmitting(true);
    const amountInt = parseInt(String(gbPackageType).replace(/[^0-9]/g, ''), 10) || 0;
    const payload = { api_token: user?.api_token, account: digitsOnly(account), email, package_type: gbPackageType, amount: amountInt, key: user?.api_token, target_number: digitsOnly(account), user_id: user?.id };
    fetch('/api/terbiumpay/payment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(user?.api_token ? { Authorization: `Bearer ${user.api_token}` } : {}) },
      body: JSON.stringify(payload)
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok || data?.success === false) throw new Error(data?.error || 'Не удалось создать платеж (GB)');
        setSubmitSuccess('Платеж создан');
        setCreatedInfo({ provider_id: 'tmcell_gb', provider_label: 'TMCELL GB', account: digitsOnly(account), amount_units: gbPackageType, email });
      })
      .catch((err) => setSubmitError(err.message || 'Ошибка создания платежа'))
      .finally(() => setSubmitting(false));
  };

  const handleCheckPayment = (pmt) => {
    const id = pmt.local_id;
    if (!id) return;
    setCheckingIds(prev => prev.includes(id) ? prev : [...prev, id]);
    const url = String(pmt.source).toLowerCase() === 'terbium' ? '/api/terbiumpay/payment/update' : '/api/bahbit/payment/update';
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(user?.api_token ? { Authorization: `Bearer ${user.api_token}` } : {}) },
      body: JSON.stringify({ states: [id] })
    })
      .then(() => loadPayments(page))
      .catch(() => {})
      .finally(() => setCheckingIds(prev => prev.filter(x => x !== id)));
  };

  /* ───────── Payment form / success ───────── */
  if (selectedProvider || selectedGb) {
    const providerLogo = selectedGb ? gbImage : selectedProvider.logo;
    const providerLabel = selectedGb ? 'TMCELL GB' : selectedProvider.label;

    return (
      <Box sx={{ maxWidth: 560 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <IconButton onClick={handleBack} size="small" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'text.primary' } }} onClick={handleBack}>
            Назад к провайдерам
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9fafb', borderRadius: 2, flexShrink: 0 }}>
              <img src={providerLogo} alt={providerLabel} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{providerLabel}</Typography>
              {!selectedGb && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Лимиты: {minAmount} — {maxAmount}</Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ p: 3 }}>
            {createdInfo ? (
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: '#dcfce7', borderRadius: 2 }}>
                  <CheckCircleIcon sx={{ color: '#22c55e', flexShrink: 0 }} />
                  <Typography sx={{ color: '#166534', fontWeight: 600 }}>Платеж успешно создан</Typography>
                </Box>
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' }}>
                  <Stack spacing={1}>
                    {[
                      ['Провайдер', createdInfo.provider_label],
                      ['Номер / счёт', createdInfo.account],
                      ['Email', createdInfo.email],
                      ['Сумма', createdInfo.amount_units],
                    ].map(([label, value]) => (
                      <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
                <Button onClick={handleBack} sx={{ borderRadius: 2, textTransform: 'none', alignSelf: 'flex-start' }}>
                  Вернуться
                </Button>
              </Stack>
            ) : (
              <Box id="topup-form" component="form" onSubmit={selectedGb ? handleSubmitGb : handleSubmit}>
                <Stack spacing={2}>
                  <TextField
                    label="Номер / счёт"
                    value={account}
                    onChange={(e) => setAccount(e.target.value)}
                    placeholder={selectedGb ? 'xxxxxxxx' : cleanedPlaceholder}
                    size="small"
                    InputProps={selectedGb
                      ? { startAdornment: <InputAdornment position="start">+993</InputAdornment> }
                      : (selectedProvider?.format?.prefix ? { startAdornment: <InputAdornment position="start">{selectedProvider.format.prefix}</InputAdornment> } : undefined)
                    }
                    error={Boolean(account) && !isAccountValid}
                    helperText={Boolean(account) && !isAccountValid ? (selectedGb ? 'Введите 8 цифр' : 'Неверный формат') : ' '}
                    required
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    size="small"
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  {!selectedGb ? (
                    <TextField
                      label={`Сумма (${minAmount} — ${maxAmount})`}
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value.replace(/\D+/g, ''))}
                      inputProps={{ min: minAmount, max: maxAmount, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                      error={Boolean(amount) && !isAmountValid}
                      helperText={Boolean(amount) && !isAmountValid ? `Допустимо от ${minAmount} до ${maxAmount}` : ' '}
                      required
                      fullWidth
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  ) : (
                    <Select
                      value={gbPackageType}
                      onChange={(e) => setGbPackageType(e.target.value)}
                      fullWidth
                      size="small"
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="1500 MB">1500 MB</MenuItem>
                      <MenuItem value="4 GB">4 GB</MenuItem>
                      <MenuItem value="20 GB">20 GB</MenuItem>
                    </Select>
                  )}
                  {submitError && <Alert severity="error" sx={{ borderRadius: 2 }}>{submitError}</Alert>}
                  {submitSuccess && <Alert severity="success" sx={{ borderRadius: 2 }}>{submitSuccess}</Alert>}
                  <Box sx={{ display: 'flex', gap: 1, pt: 0.5 }}>
                    <Button onClick={handleBack} sx={{ borderRadius: 2, textTransform: 'none' }}>Назад</Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={submitting || !isAccountValid || (!selectedGb && !isAmountValid)}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, flex: 1 }}
                    >
                      {submitting ? <CircularProgress size={18} color="inherit" /> : 'Перейти к оплате'}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    );
  }

  /* ───────── Provider list + payments ───────── */
  return (
    <Box sx={{ maxWidth: 960 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Пополнение</Typography>
        {balance != null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' }}>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>Баланс</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {(balance / 100).toLocaleString()}
              </Typography>
            </Box>
            <Tooltip title="Обновить баланс">
              <IconButton size="small" onClick={handleRefreshBalance} disabled={reloadingBalance} sx={{ ml: 0.5 }}>
                {reloadingBalance
                  ? <CircularProgress size={16} />
                  : <AutorenewIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Provider grid */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5 }}>
        Провайдер
      </Typography>

      {loading && (
        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      {!loading && !error && (
        <Grid container spacing={1.5} sx={{ mb: 4 }}>
          {(() => {
            const nodes = providers.map(p => (
              <Grid item xs={12} sm={6} md={4} key={p.id}>
                <Paper
                  elevation={0}
                  onClick={() => handleSelect(p)}
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2.5,
                    transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.1s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9fafb', borderRadius: 1.5, flexShrink: 0 }}>
                    <img src={p.logo} alt={p.label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.label}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{p.min / 100} — {p.max / 100}</Typography>
                  </Box>
                </Paper>
              </Grid>
            ));
            const gbNode = (
              <Grid item xs={12} sm={6} md={4} key="tmcell-gb">
                <Paper
                  elevation={0}
                  onClick={handleSelectGb}
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2.5,
                    transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.1s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9fafb', borderRadius: 1.5, flexShrink: 0 }}>
                    <img src={gbImage} alt="TMCELL GB" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>TMCELL GB</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Пакеты</Typography>
                  </Box>
                </Paper>
              </Grid>
            );
            const insertAt = Math.min(2, nodes.length);
            nodes.splice(insertAt, 0, gbNode);
            return nodes;
          })()}
        </Grid>
      )}

      {/* Payments section */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Платежи
        </Typography>
        <TextField
          size="small"
          label="ID заказа"
          placeholder="Напр.: 12345 или 1,2,3"
          value={searchIdInput}
          onChange={e => setSearchIdInput(e.target.value)}
          sx={{ width: 260, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      </Box>

      {paymentsLoading && (
        <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}
      {paymentsError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{paymentsError}</Alert>}

      {!paymentsLoading && !paymentsError && (
        <>
          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fafafa' }}>
                    {['local_id', 'Источник', 'Аккаунт', 'Пополнено', 'Состояние', 'Оператор', 'Примечание', ''].map(h => (
                      <TableCell key={h} align={h === '' ? 'right' : 'left'} sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>Нет данных</TableCell>
                    </TableRow>
                  ) : (
                    payments.map(pmt => (
                      <TableRow key={pmt.id ?? `${pmt.local_id}-${pmt.account}`} sx={{ '&:hover': { bgcolor: '#f9fafb' }, transition: 'background-color 0.15s' }}>
                        <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontFamily: 'monospace' }}>{pmt.local_id}</TableCell>
                        <TableCell sx={{ fontSize: '0.8125rem' }}>{pmt.source || '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.8125rem', fontFamily: 'monospace' }}>{pmt.account}</TableCell>
                        <TableCell sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{formatPaymentAmount(pmt)}</TableCell>
                        <TableCell><PaymentStateBadge state={pmt.state} /></TableCell>
                        <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{pmt.operator_name || 'Вы'}</TableCell>
                        <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {pmt.error_message || '—'}
                        </TableCell>
                        <TableCell align="right">
                          {Number(pmt.state) === 1 && pmt.local_id && pmt.local_id !== '-' && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleCheckPayment(pmt)}
                              disabled={checkingIds.includes(pmt.local_id)}
                              sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: '0.75rem', minWidth: 84 }}
                            >
                              {checkingIds.includes(pmt.local_id) ? <CircularProgress size={14} /> : 'Проверить'}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
            <Button
              size="small"
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Назад
            </Button>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Стр. {page}{totalPages ? ` / ${totalPages}` : ''}
            </Typography>
            <Button
              size="small"
              disabled={!!(totalPages && page >= totalPages)}
              onClick={() => setPage(p => p + 1)}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Дальше
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
